import React, { useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import usePopover from "components/common/usePopover";
import TherapyPicker from "./TherapyPicker";
import VerifyPrescription from "./VerifyPrescription";
import UserService from "modules/user/service/UserService";
import DoctorCategory from "constants/type/user/DoctorCategory";
import StatusSelect from "modules/appointments/components/StatusSelect";
import {
  STATUS_LABEL,
  STATUS_TONE,
  END_STATUSES,
} from "modules/appointments/helper/appointmentConstants";
import { formatTimeSlot } from "modules/appointments/helper/formatters";
import {
  formatAgeLabel,
  formatGenderAge,
  formatLongDate,
} from "../helper/patientFormatters";
import { PROFILE_TYPE_CHIP_CLASS, profileTypeLabel } from "../helper/patientConstants";
import useIsOverflowing from "../helper/useIsOverflowing";
import PreferredLanguagesPopover from "./PreferredLanguagesPopover";
import DeliveryDateLine from "./DeliveryDateLine";
import DocumentsMenu from "./DocumentsMenu";
import AppointmentDocumentsMenu from "./AppointmentDocumentsMenu";
import PatientDetailsDialog from "./PatientDetailsDialog";
import CallNoteModal from "./CallNoteModal";
import UpdateBookingFor from "./UpdateBookingFor";
import "../styles/addressBook.scss";
import { callNoteVariant } from "../helper/callNoteVariant";

/**
 * The edit pencil beside the patient's name — Figma 5125:4527 ("edit").
 *
 * The path data is the EXPORTED vector, taken verbatim from that node's asset
 * rather than redrawn: a hand-approximated pencil is a different icon. Inlined
 * rather than <img>, because the export's URL points at Figma's Dev Mode
 * localhost server, which does not exist at runtime.
 *
 * Two things dropped from the raw export, both no-ops: Figma wraps the path in
 * a bounding-box <mask> that clips to the same 16x16 the path already sits
 * inside, and hard-codes fill="#F97F0F". `currentColor` is used instead so the
 * colour comes from CSS, where $orange is that same value.
 */
const EditIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3.3332 12.6665H4.1742L10.9985 5.8422L10.1575 5.0012L3.3332 11.8255V12.6665ZM2.3332 13.6665V11.4102L11.1269 2.62037C11.2276 2.52881 11.3389 2.45809 11.4607 2.4082C11.5826 2.3582 11.7104 2.3332 11.844 2.3332C11.9777 2.3332 12.1071 2.35693 12.2324 2.40437C12.3577 2.45181 12.4686 2.52726 12.5652 2.6307L13.3794 3.45504C13.4828 3.55159 13.5565 3.6627 13.6005 3.78837C13.6445 3.91404 13.6665 4.0397 13.6665 4.16537C13.6665 4.29948 13.6436 4.42743 13.5979 4.5492C13.5521 4.67109 13.4793 4.78243 13.3794 4.8832L4.58954 13.6665H2.3332ZM10.5707 5.42904L10.1575 5.0012L10.9985 5.8422L10.5707 5.42904Z" />
  </svg>
);
EditIcon.propTypes = { className: PropTypes.string };

/** The phonebook/contact-card glyph (V4 `imgPhonebook`, 2428:4741 — 30px). */
const Phonebook = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="6.5" y="3" width="14.5" height="18" rx="2.5" />
    <path d="M3.5 8h3M3.5 12h3M3.5 16h3" />
    <circle cx="13.7" cy="10.2" r="2.3" />
    <path d="M10 16.6a3.9 3.9 0 017.4 0" />
  </svg>
);

/**
 * `valueRef` + `tooltip` are the clipped-text pair, used by Reason and Account
 * name.
 *
 * The ref goes on `.ph-col-value` because that is the node carrying the
 * ellipsis (`.ph-col.is-clip .ph-col-value` sets overflow: hidden), and the
 * `.li-dm-tt` class goes on `.ph-col`, which sets no overflow — on the value
 * the tooltip's ::after would be clipped by the same box that clipped the text.
 * The class is added whenever a caller opts in; `data-tt` only when the text is
 * ACTUALLY cut off, so a short reason fires nothing. Same tooltip the section
 * tables use — one definition, `.li-dm-tt` in sections.scss.
 */
const Labelled = ({ label, children, className, valueRef, tooltip }) => (
  <div
    className={`ph-col${className ? " " + className : ""}${valueRef ? " li-dm-tt" : ""}`}
    data-tt={tooltip || undefined}
  >
    <span className="ph-col-label">{label}</span>
    <div className="ph-col-value" ref={valueRef}>{children}</div>
  </div>
);
Labelled.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  valueRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  tooltip: PropTypes.string,
};

/** A row's "open" chevron (points right). */
const ChevronRight = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 6 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 1l4 4-4 4" />
  </svg>
);
ChevronRight.propTypes = { className: PropTypes.string };

/**
 * One touchpoint column: the newest entry inline, then a "+N more" that opens a
 * dropdown of ALL entries in this column (Figma 2646:3942 / 2646:3969). Each row
 * is name · date (· start time for upcoming) · an open chevron; clicking one
 * opens that appointment. Entries are already capped at 3 by the owner ("max 3").
 */
const TouchpointCell = ({ label, entries, empty, onOpen, state }) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));
  const has = entries && entries.length;
  /* An unfinished fetch is not an empty result. Until it lands the cell says so
     rather than asserting "No past appointments" over a list still loading. */
  const pending = state === "loading";
  const more = has ? entries.length - 1 : 0;
  const rowLabel = (e) => (e.time ? `${e.date} · ${e.time}` : e.date);

  return (
    <div className="ph-tp-cell">
      <span className="ph-tp-label">{label}</span>
      <div className="ph-tp-val">
        {has ? (
          <>
            {/* The newest touchpoint is itself openable — otherwise a column
                with a single entry (no "+N more") would have no click target. */}
            {onOpen && entries[0].id ? (
              <button
                type="button"
                className="ph-tp-open"
                onClick={() => onOpen(entries[0].id)}
                title="Open this appointment"
              >
                <span className="ph-tp-line">{entries[0].name}</span>
                <span className="ph-tp-date">{entries[0].date}</span>
              </button>
            ) : (
              <>
                <span className="ph-tp-line">{entries[0].name}</span>
                <span className="ph-tp-date">{entries[0].date}</span>
              </>
            )}
            {more > 0 && (
              <button
                type="button"
                ref={triggerRef}
                className="ph-tp-more"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
              >
                +{more} more
              </button>
            )}
          </>
        ) : (
          <span className="ph-tp-empty">{pending ? "Loading\u2026" : state === "error" ? "Couldn\u2019t load" : empty}</span>
        )}
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="ph-tp-menu"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            role="menu"
            aria-label={label}
          >
            {entries.map((e) => (
              <button
                key={e.id}
                type="button"
                className="ph-tp-menu-row"
                role="menuitem"
                disabled={!e.id || !onOpen}
                onClick={() => {
                  setOpen(false);
                  if (onOpen && e.id) onOpen(e.id);
                }}
              >
                <span className="ph-tp-menu-name">{e.name}</span>
                <span className="ph-tp-menu-date">{rowLabel(e)}</span>
                <ChevronRight className="ph-tp-menu-chev" />
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};
TouchpointCell.propTypes = {
  label: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      date: PropTypes.string,
      time: PropTypes.string,
    })
  ),
  empty: PropTypes.string.isRequired,
  /** Opens an appointment by id (from a dropdown row). */
  onOpen: PropTypes.func,
};

/**
 * V4 strip 3 — "Band · Touchpoints" (Figma 2428:4788). Past and upcoming
 * appointments only: the document chips this band used to carry are gone, and
 * DOCUMENTS is now a "View all" trigger at the right of strip 2.
 *
 * Live: PatientDetails derives these from the patient's other appointments
 * (helper/touchpoints.js) — the "hardcoded placeholder" note this comment used
 * to carry has been false since that wiring landed.
 *
 * Empty states keep the V4 SPEC's copy (2359:3242): "No past appointments" /
 * "None scheduled" — but only once `state` says the fetch finished. Both lists
 * start as [], so showing that copy unconditionally answered a question that
 * had not been asked yet.
 */
const Touchpoints = ({ past, upcoming, onOpen, state, patientId }) => (
  <div className="ph-touchpoints">
    <TouchpointCell
      label="Past appointments"
      entries={past}
      empty="No past appointments"
      state={state}
      onOpen={onOpen}
    />
    <span className="ph-tp-div" aria-hidden="true" />
    <TouchpointCell
      label="Upcoming appointments"
      entries={upcoming}
      empty="None scheduled"
      state={state}
      onOpen={onOpen}
    />
    {/* The board's spacer (3015:4482): everything above is left-packed and the
        documents cell rides the right edge. */}
    <span className="ph-tp-sp" aria-hidden="true" />
    {/* ALL DOCUMENTS (5125:4549) — every document the PATIENT holds. It used to
        sit in the appointment band, which put a patient-scoped control among
        appointment facts; the board moves it here, beside the other
        patient-history cells, and gives its old slot to "Update booking for". */}
    <div className="ph-tp-cell ph-tp-docs">
      <span className="ph-tp-label">All documents</span>
      <div className="ph-tp-val">
        <DocumentsMenu patientId={patientId} />
      </div>
    </div>
  </div>
);
Touchpoints.propTypes = {
  past: PropTypes.arrayOf(PropTypes.object),
  upcoming: PropTypes.arrayOf(PropTypes.object),
  /** "loading" | "ok" | "error" — an unfinished fetch is not an empty result. */
  state: PropTypes.string,
  onOpen: PropTypes.func,
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

/** Figma's call-note mark on the trigger (3131:16355). */
const NoteIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 2.2h9c.5 0 .8.4.8.8v8.2l-2.6 2.6H3.5c-.5 0-.8-.4-.8-.8V3c0-.4.3-.8.8-.8Z" />
    <path d="M5.4 6h5.2M5.4 8.8h3.2" />
  </svg>
);

/**
 * The patient header — three strips: identity/actions, the appointment, and
 * past/upcoming touchpoints. Source: Figma 2428:4723 "Header · Appointment bar ·
 * V4 — edge-case hardened".
 *
 * Data (condition chip, past/upcoming touchpoints) comes from PatientDetails —
 * the condition from the primary-therapy assessment, the touchpoints from the
 * patient's other appointments. The several ACTIONS here (Call, contact card,
 * Reschedule, the profile switcher, Documents) are still rendered disabled
 * rather than omitted, so the screen keeps the shape design drew while being
 * honest that phr can't perform them yet; each carries its reason inline.
 */
const PatientHeaderCard = ({
  patient,
  languages,
  languagesState,
  appointment,
  reasonName,
  onChangeStatus,
  /* Set to the status just applied when a change SUCCEEDS; opens the call
     note automatically with that status shown. Null on every other path. */
  callNoteForStatus,
  onCloseCallNote,
  updating,
  primaryLabel,
  onPrimaryAction,
  condition,
  therapyTag,
  onTherapyChanged,
  pastAppointments,
  upcomingAppointments,
  touchpointsState,
  onLanguagesSaved,
  onPatientUpdated,
  onEditDetails,
  onReschedule,
  rescheduleDisabledReason,
  onOpenAppointment,
  onOpenAppointmentDetails,
  onCreateHealthPlan,
  call,
  onUpdateBookingFor,
  bookingForBlockedReason,
  originalBookingName,
  collapsed,
  compact,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  /* The LABEL, not the number: months under a year (formatAgeLabel). */
  const age = formatAgeLabel(patient.dateOfBirth, patient.age);
  const fullName = [patient.firstName, patient.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  /* The user-type tag. A patient has exactly one type, so this is one chip or
     none — Default and a missing value both render nothing. */
  const typeClass = PROFILE_TYPE_CHIP_CLASS[patient.profileType];
  const typeLabel = typeClass ? profileTypeLabel(patient.profileType) : "";

  /* Reason is the one header cell whose text is free-form and routinely longer
     than its column, so it gets the sections' hover tooltip when — and only
     when — it is actually clipped. Measured, not inferred from length: the
     column is a flex slack-taker (.ph-col.is-reason), so the same string is cut
     off at one window width and not another, and ResizeObserver re-measures as
     that changes. */
  const reasonText =
    [reasonName, appointment.reasonDetails].filter(Boolean).join(" — ") || "—";
  const [reasonRef, reasonClipped] = useIsOverflowing(reasonText);
  /* The account name is a client's own label and can outrun its fixed column
     ("New Dev1 Account …") — the same measured hover as Reason (Nittin,
     2026-10-07). */
  const accountName = appointment.accountName || "—";
  const [accountRef, accountClipped] = useIsOverflowing(accountName);

  const timeRange = formatTimeSlot(appointment.timeSlot, appointment.serviceDuration);
  const [callNote, setCallNote] = useState(false);
  const [isNutritionist] = useState(
    () => new UserService().doctorCategory() === DoctorCategory.NUTRITIONIST
  );
  const when = [formatLongDate(appointment.appointmentDate), timeRange]
    .filter(Boolean)
    .join(", ");

  return (
    <section className={`patient-header${collapsed ? " is-collapsed" : ""}`}>
      {/* ---------- strip 1: identity + actions ---------- */}
      <div className="ph-identity">
        <div className="ph-who">
          <div className="ph-chips">
            <span className="ph-chip">PID {patient.id}</span>
            {/* profileType is a classification enum (0 default, 1 premium, 2 HR
                rep, 3 QA, 4 sensitive) — not a plan tier, which doesn't exist.
                Read-only: bridge's edit modal has a constructor bug that
                silently downgrades the patient on save.

                This used to render the PREMIUM chip and nothing else, so an HR
                rep, a QA account and a sensitive-flagged patient all looked
                like ordinary patients here. All four classes are shown now
                (Figma "Tag · User type", 3015:4411); the colours and the
                default-gets-no-chip rule are argued in patientConstants. */}
            {typeLabel && <span className={`ph-chip ${typeClass}`}>{typeLabel}</span>}
            {/* The patient's primary-therapy condition (2639:3452), and the
                control that changes it. bridge's original patient screen has
                always had this dropdown; the record header only ever displayed
                the value. Rendered even with no therapy set — the chip then
                reads "Set therapy", because a header that hides the control
                when it is most needed is the wrong trade. */}
            <TherapyPicker
              patientId={patient && (patient.id || patient.patientID)}
              tag={therapyTag}
              label={condition}
              onChanged={onTherapyChanged}
            />
            {/* "Verify prescription" (Figma 3015:4405) — legacy's Verify
                Patient flow. On Orbit it is a nutritionist's job, so only a
                nutritionist sees it (product, 2026-09-17); bridge shows it to
                everyone. */}
            {isNutritionist && <VerifyPrescription patient={patient} />}
          </div>

          <div className="ph-name-row">
            <div className="ph-name-trigger">
              {/* Name + edit pencil open the Patient-details dialog (3015:5175).
                  NOT the profile switcher the earlier design implied — that
                  needs a linked-profiles lookup keyed on phone number, which
                  phr does not load. */}
              <button
                type="button"
                className="ph-name-btn"
                onClick={() => (onEditDetails ? onEditDetails() : setDetailsOpen(true))}
                title="View and edit patient details"
              >
                <h1 className="ph-name">{fullName}</h1>
                <span className="ph-edit-btn" aria-hidden="true">
                  <EditIcon className="ph-edit" />
                </span>
              </button>
            </div>
            <span className="ph-meta">{formatGenderAge(patient.gender, age)}</span>
            {/* "Original appointment" (3820:3089) — only while "Update booking
                for" has pointed this record away from the member the appointment
                is booked for; names that member. */}
            {originalBookingName && (
              <span
                className="ph-orig-booking"
                title={`This appointment is booked for ${originalBookingName}`}
              >
                <span className="ph-orig-booking-label">Original appointment</span>
                <span className="ph-orig-booking-name">{originalBookingName}</span>
              </span>
            )}
          </div>

          {/* Editable: the line is the trigger for the preferred-languages
              popover (Figma 2646:3793). Always shown — when the patient has none
              it invites adding the first. */}
          <PreferredLanguagesPopover
            patientId={patient.id}
            languages={languages}
            languagesState={languagesState}
            onSaved={onLanguagesSaved}
          />
          {/* On Pregnancy therapy, the expected delivery date — as legacy
              bridge's patient screen showed it. Nothing otherwise. */}
          <DeliveryDateLine patientId={patient.id} therapyTag={therapyTag} therapyLabel={condition} />
        </div>

        <div className="ph-actions">
          {/* RENDERED ONLY WHERE THE OWNER WIRES THE CALL FLOW — the same rule
              as "Create health plan" below. `call` is the shared useCallFlow
              hook, passed by the page that can place calls (PatientDetails):
              Call dials the PRIMARY number, the phonebook opens the address
              book.

              The Rx screen passes none, and used to get both controls
              permanently greyed with a "Bridge staff only" tooltip. A disabled
              button still offers something the screen cannot do, so it is not
              drawn there at all (product, 2026-09-12) — the same call as the
              Appointment-details call group and the call-note Share button.
              `.ph-actions` is a flex row with a gap, so the remaining controls
              simply close up; the divider after Call note is unrelated to this
              group. */}
          {call && (
            <div className="ph-call-group">
              <button
                type="button"
                className="ph-call"
                disabled={!call.canCallPrimary}
                onClick={call.callPrimary}
                title={call.callPrimaryTitle}
              >
                Call
              </button>
              <button
                type="button"
                className="ph-phonebook"
                onClick={call.openAddressBook}
                /* Named as the dialog it opens is titled (product, 2026-09-17). */
                title="Phonebook"
                aria-label="Phonebook"
              >
                <Phonebook />
              </button>
            </div>
          )}

          {/* Call note (3131:16354) — sits between the call group and the
              status control, per 3015:6268. */}
          <button
            type="button"
            className="ph-callnote"
            aria-haspopup="dialog"
            onClick={() => setCallNote(true)}
          >
            <NoteIcon />
            Call note
          </button>

          <span className="ph-actions-div" aria-hidden="true" />

          {/* V4 moves the status control up from strip 2. Same StatusSelect —
              same portalled menu, same onChange — only re-skinned in CSS. */}
          <div className="ph-status">
            <StatusSelect
              value={appointment.status}
              tone={STATUS_TONE[appointment.status] || "progress"}
              label={STATUS_LABEL[appointment.status] || "—"}
              labels={STATUS_LABEL}
              tones={STATUS_TONE}
              disabled={updating}
              readOnly={END_STATUSES.indexOf(appointment.status) !== -1}
              onChange={onChangeStatus}
            />
          </div>

          {/* "Create health plan" sits beside Create Rx, same design (Nittin,
              2026-07-23). Rendered only where the owner wires it (the patient
              screen) — the Rx screen passes no handler and shows no button. */}
          {onCreateHealthPlan && (
            <button
              type="button"
              className="ph-btn ph-btn-primary"
              onClick={onCreateHealthPlan}
            >
              Create health plan
            </button>
          )}

          {/* The primary CTA is contextual: "Create Rx" on the patient screen
              (opens the review-prescription screen), "Publish Rx" on that
              screen itself. Without an action it stays disabled rather than
              navigating nowhere.

              Rendered only when the owner asked for one. The patient screen
              WITHHOLDS it for a non-prescriber, who gets "Create health plan"
              instead — the two are mutually exclusive there. A default "Create
              Rx" here would have put a disabled prescribe button in front of a
              nutritionist, which is worse than no button at all.
              The Rx screen keeps `primaryLabel` set while saving even though it
              drops the handler, so checking both is what keeps "Publishing…"
              on screen. */}
          {(onPrimaryAction || primaryLabel) && (
            <button
              type="button"
              className="ph-btn ph-btn-primary"
              disabled={!onPrimaryAction}
              onClick={onPrimaryAction}
              title={onPrimaryAction ? undefined : "Not wired up yet"}
            >
              {primaryLabel || "Create Rx"}
            </button>
          )}
        </div>
      </div>

      {/* Everything below the identity row folds away when the header is
          pinned. Wrapped in ONE grid so the collapse can animate to the real
          content height (grid-template-rows 1fr -> 0fr) instead of to an
          arbitrary max-height, which is what made it snap: a 260px max-height
          travelling to 0 spends most of its duration above the ~110px the
          content actually occupies, so nothing moves and then everything does
          at once. */}
      {/* COMPACT drops this block entirely rather than collapsing it. `collapsed`
          is the scroll-pin: the content still exists and animates back. Create Rx
          has no appointment strip and no touchpoints at all — the appointment is
          the one being prescribed for, and its own screen is a click away — so
          rendering them hidden would keep fetching and measuring content nobody
          can reach. */}
      {!compact && (
      <div className="ph-fold">
        <div className="ph-fold-inner">
      <span className="ph-rule" aria-hidden="true" />

      {/* ---------- strip 2: the appointment ---------- */}
      <div className="ph-appointment">
        <Labelled label="Appt ID">
          {/* Links to the appointment-details screen (Figma 2618:4041). Plain
              text when the owner didn't wire a handler (e.g. the Rx screen). */}
          {onOpenAppointmentDetails ? (
            <button
              type="button"
              className="ph-appt-link"
              onClick={onOpenAppointmentDetails}
            >
              {appointment.id}
            </button>
          ) : (
            appointment.id
          )}
        </Labelled>

        <Labelled label="Appointment" className="is-wide">
          <span>{when || "—"}</span>
          {/* Bridge's Reschedule button: NOT RENDERED for a blocked, gym or
              closed appointment (no disabled state, no tooltip). Inert only
              where the owner wired no handler (the Rx screen). */}
          {!rescheduleDisabledReason && (
            <button
              type="button"
              className="ph-chip is-action"
              onClick={onReschedule}
              disabled={!onReschedule}
              title={onReschedule ? undefined : "Not available here"}
            >
              Reschedule
            </button>
          )}
        </Labelled>

        <Labelled
          label="Account name"
          className="is-clip is-account"
          valueRef={accountRef}
          tooltip={accountClipped ? accountName : undefined}
        >
          {accountName}
        </Labelled>

        <Labelled label="Account code" className="is-clip is-account">
          {appointment.accountCode || "—"}
        </Labelled>

        {/* reasonId only on the wire — the name comes from the master list,
            fetched once and mapped. reasonDetails is the patient's own text,
            which is free-form and routinely longer than the column: it is the
            one cell here that genuinely needs the hover. */}
        <Labelled
          label="Reason"
          className="is-clip is-reason"
          valueRef={reasonRef}
          tooltip={reasonClipped ? reasonText : undefined}
        >
          {reasonText}
        </Labelled>

        {/* The files attached to THIS appointment (Figma 3015:4458). Distinct
            from the patient-wide list beside it, and read from a different
            endpoint — /v1/appointment/document/{id}. */}
        <Labelled label="Appointment documents" className="is-docs is-appt-docs">
          <AppointmentDocumentsMenu appointmentId={appointment.id} />
        </Labelled>

        {/* "Update booking for" (5125:4576) takes the slot the patient-wide
            Documents field used to hold; that field is now the ALL DOCUMENTS
            cell in the touchpoints band. Live where the owner wired the switch
            (the patient screen); elsewhere it keeps the header's
            disabled-with-a-reason convention. */}
        {onUpdateBookingFor ? (
          <UpdateBookingFor
            appointment={appointment}
            currentMemberId={patient.id}
            onConfirm={onUpdateBookingFor}
            blockedReason={bookingForBlockedReason}
          />
        ) : (
          <button
            type="button"
            className="ph-update-booking"
            disabled
            title="Changing who this appointment is booked for isn't available here"
          >
            Update booking for
          </button>
        )}
      </div>

      {/* ---------- strip 3: touchpoints (V4) ---------- */}
      <Touchpoints
        past={pastAppointments}
        upcoming={upcomingAppointments}
        state={touchpointsState}
        onOpen={onOpenAppointment}
        patientId={patient.id}
      />
        </div>
      </div>
      )}

      {/* `patient` and `appointment` are load-bearing, not decoration:
          patient.id addresses the chat push and the call-plan lookup, and the
          appointment supplies the template's status and the lookup's date
          window. The modal had neither until now. */}
      {/* Two ways in, and they differ in exactly one thing.
            · the Call note button  -> no status is known, so none is shown;
            · a status change that just SUCCEEDED -> opened automatically, with
              the status the operator just set.
          The appointment's own stored status is deliberately NOT used for the
          button path: it is a BOOKING state, not a call outcome, so rendering
          it as "Status - Completed" would assert something nobody said. */}
      {(callNote || callNoteForStatus != null) && (
        <CallNoteModal
          variant={
            callNoteForStatus != null ? callNoteVariant(callNoteForStatus) : undefined
          }
          /* The status the operator just set, in its own words. Without this the
             modal printed whichever of five buckets the status fell into. */
          statusLabel={
            callNoteForStatus != null ? STATUS_LABEL[callNoteForStatus] : undefined
          }
          patient={patient}
          appointment={appointment}
          onClose={() => {
            setCallNote(false);
            if (onCloseCallNote) onCloseCallNote();
          }}
        />
      )}

      {detailsOpen && (
        <PatientDetailsDialog
          patient={patient}
          onClose={() => setDetailsOpen(false)}
          onSaved={(updated) => {
            setDetailsOpen(false);
            if (onPatientUpdated) onPatientUpdated(updated);
          }}
        />
      )}

      {/* The address book, from the page's useCallFlow. It stays mounted HERE,
          inside the header, as it always was: it does not portal, so this keeps
          the pinned header expanded while it is open and keeps it in
          .ph-sticky's stacking context. */}
      {call && call.addressBookUI}
</section>
  );
};

PatientHeaderCard.propTypes = {
  /** Pinned to the top: shed the secondary rows until hovered. */
  collapsed: PropTypes.bool,
  compact: PropTypes.bool,

  patient: PropTypes.object.isRequired,
  languages: PropTypes.array,
  languagesState: PropTypes.string,
  appointment: PropTypes.object.isRequired,
  reasonName: PropTypes.string,
  onChangeStatus: PropTypes.func.isRequired,
  /** A NAVIGO_STATUS, or null. See the comment where the modal is rendered. */
  callNoteForStatus: PropTypes.number,
  onCloseCallNote: PropTypes.func,
  updating: PropTypes.bool,
  /** Primary CTA label; defaults to "Create Rx". */
  primaryLabel: PropTypes.string,
  /** Primary CTA handler. Omit to leave the button disabled. */
  onPrimaryAction: PropTypes.func,
  /** Primary-therapy condition name. Empty renders the "Set therapy" chip. */
  condition: PropTypes.string,
  /** Current therapy's tag, so the picker can mark it. */
  therapyTag: PropTypes.string,
  /** (tag, text) => void, after a successful change. */
  onTherapyChanged: PropTypes.func,
  /** Touchpoints — [{ name, date }], newest-first / soonest-first. */
  pastAppointments: PropTypes.arrayOf(PropTypes.object),
  /** "loading" | "ok" | "error" for the touchpoints strip. */
  touchpointsState: PropTypes.string,
  upcomingAppointments: PropTypes.arrayOf(PropTypes.object),
  /** Called after preferred languages are saved, so the owner can refetch. */
  onLanguagesSaved: PropTypes.func,
  /** Fired after the Patient-details dialog saves, so the owner can refetch. */
  onPatientUpdated: PropTypes.func,
  /**
   * Given, the name / pencil calls THIS instead of opening the header's own
   * Patient-details dialog — for a screen that mounts the dialog itself because
   * something else on it opens the dialog too (Create Rx's "Update"). One
   * mount per screen: two copies could both be opened (keyboard reaches the
   * second trigger past the first's scrim), and the lower one, re-seeded from
   * the saved record, lights Save with the pre-save values of a full-record
   * replace.
   */
  onEditDetails: PropTypes.func,
  /** Opens the reschedule modal. Omit to leave the button disabled. */
  onReschedule: PropTypes.func,
  /**
   * When non-empty, the Reschedule button is disabled and this string is its
   * tooltip (a blocked / gym / closed appointment — see rescheduleBlockReason).
   */
  rescheduleDisabledReason: PropTypes.string,
  /** Opens a past/upcoming appointment by id from the touchpoints dropdown. */
  onOpenAppointment: PropTypes.func,
  /** Opens the appointment-details screen (the Appt ID becomes a link). */
  onOpenAppointmentDetails: PropTypes.func,
  /** Opens the health-plan editor; the button renders only when passed. */
  onCreateHealthPlan: PropTypes.func,
  /**
   * "Update booking for" — (member) => Promise, saving phr's booking-for
   * mapping. Omit to leave the button disabled.
   */
  onUpdateBookingFor: PropTypes.func,
  /** When set, "Update booking for" is held and this says why (hover + click). */
  bookingForBlockedReason: PropTypes.string,
  /** Who the appointment is booked for, while it is switched; "" hides the chip. */
  originalBookingName: PropTypes.string,
  /**
   * The shared call flow (useCallFlow) — its callPrimary / canCallPrimary /
   * callPrimaryTitle / openAddressBook drive Call and the phonebook, and its
   * addressBookUI is mounted inside the header. Absent on screens that cannot
   * place calls, which keeps both disabled.
   */
  call: PropTypes.shape({
    callPrimary: PropTypes.func,
    canCallPrimary: PropTypes.bool,
    callPrimaryTitle: PropTypes.string,
    openAddressBook: PropTypes.func,
    addressBookUI: PropTypes.node,
  }),
};

PatientHeaderCard.defaultProps = {
  condition: "",
  pastAppointments: [],
  upcomingAppointments: [],
  rescheduleDisabledReason: "",
};

export default PatientHeaderCard;
